import moment from 'moment'
import React from 'react'
import styled, {
  FONT_SIZES,
  labelWhenSmallProps,
  MQ,
} from '../../constants/theme'
import { SelectorIcon } from '../elements/Icons'
import {
  Bar,
  BasicType,
  bottomBorderForType,
  ElementState,
  ElementType,
  FormElementProps,
  QuestionType,
  RightIconContainer,
} from './FormElementParts'

export interface DateContainerProps {
  elementState?: ElementState
  type: BasicType | ElementType | QuestionType
}

interface DateSelectProps {
  hasLeftIcon?: boolean
}

interface DateOption {
  label: string
  value: number
}

enum Parts {
  DAY = 'day',
  MONTH = 'month',
  YEAR = 'year',
}

export const getMonths = (): DateOption[] =>
  [{ label: 'Month', value: -1 }].concat(
    moment.months().map((month, index) => {
      return { label: month, value: index }
    }),
  )

export const getYears = (): DateOption[] => {
  return Array(110)
    .fill(0)
    .map((_e, i) => {
      const derived = moment().year() + 1 - i
      if (i === 0) {
        return { label: 'Year', value: -1 }
      }
      return { label: `${derived}`, value: derived }
    })
}

export const getDays = (daysInMonth: number): DateOption[] => {
  return Array(daysInMonth + 1)
    .fill(0)
    .map((_, index) => {
      if (index === 0) {
        return { label: 'Day', value: -1 }
      }
      return { label: `${index}`, value: index }
    })
}

const getNewDate = (
  date: moment.Moment,
  name: string,
  value: number,
): moment.Moment => {
  switch (name) {
    case Parts.DAY:
      return date.date(value)
    case Parts.MONTH:
      return date.month(value)
    case Parts.YEAR:
      return date.year(value)
    default:
      return date
  }
}

const DateElement: React.FC<FormElementProps> = ({
  disabled,
  elementState,
  icon,
  label,
  name,
  onChange,
  type,
  value,
}) => {
  const dayRef = React.useRef<HTMLSelectElement>(null)
  const monthRef = React.useRef<HTMLSelectElement>(null)
  const yearRef = React.useRef<HTMLSelectElement>(null)
  const callChange = React.useCallback(
    val => {
      if (onChange) {
        onChange({ name, value: val.format('YYYY-MM-DD') })
      }
    },
    [name, onChange],
  )
  const handleChange = e => {
    e.preventDefault()
    const oldDate = moment(value || undefined)
    const newDate = getNewDate(oldDate, e.target.name, e.target.value)
    callChange(newDate)
  }
  React.useEffect(() => {
    if (dayRef.current && monthRef.current && yearRef.current) {
      const oldDate = moment(value || undefined)
      let newDate = getNewDate(oldDate, Parts.DAY, Number(dayRef.current.value))
      newDate = getNewDate(newDate, Parts.MONTH, Number(monthRef.current.value))
      newDate = getNewDate(newDate, Parts.YEAR, Number(yearRef.current.value))
      callChange(newDate)
    }
  }, [callChange, value])
  const date = moment(value || undefined)
  const day = date.date()
  const month = date.month()
  const year = date.year()
  const days = getDays(date.daysInMonth())
  return (
    <DateGroup>
      <DateContainer elementState={elementState} type={type}>
        <DateSelect
          aria-label={`month ${label}`}
          className="MonthSelect"
          disabled={disabled}
          hasLeftIcon
          name={Parts.MONTH}
          onChange={handleChange}
          ref={monthRef}
          value={month}
        >
          {getMonths().map(m => {
            return (
              <option
                aria-selected={month === m.value ? true : false}
                key={`option_${m.value}`}
                value={m.value}
              >
                {m.label}
              </option>
            )
          })}
        </DateSelect>
        <RightIconContainer>
          <SelectorIcon />
        </RightIconContainer>
        <Bar />
      </DateContainer>
      <DateContainer elementState={elementState} type={type}>
        <DateSelect
          aria-label={`day ${label}`}
          className="DaySelect"
          disabled={disabled}
          name={Parts.DAY}
          onChange={handleChange}
          ref={dayRef}
          value={day}
        >
          {days.map(d => {
            return (
              <option
                aria-selected={day === d.value ? true : false}
                key={`option_${d.value}`}
                value={d.value}
              >
                {d.label}
              </option>
            )
          })}
        </DateSelect>
        <RightIconContainer>
          <SelectorIcon />
        </RightIconContainer>
        <Bar />
      </DateContainer>
      <DateContainer
        className={`icon-${icon}`}
        elementState={elementState}
        type={type}
      >
        <DateSelect
          aria-label={`year ${label}`}
          className="YearSelect"
          disabled={disabled}
          name={Parts.YEAR}
          onChange={handleChange}
          ref={yearRef}
          value={year}
        >
          {getYears().map(y => {
            return (
              <option
                aria-selected={year === y.value ? true : false}
                key={`option_${y.value}`}
                value={y.value}
              >
                {y.label}
              </option>
            )
          })}
        </DateSelect>
        <RightIconContainer>
          <SelectorIcon />
        </RightIconContainer>
        <Bar />
      </DateContainer>
    </DateGroup>
  )
}

export const DateGroup = styled.div({
  [MQ.LARGE_DESKTOP]: {
    [`html[style*="${FONT_SIZES[2].size}"] &`]: {
      '& > div': {
        marginRight: 20,
      },
      '& > div:last-child': {
        marginRight: 0,
      },
      display: 'flex',
    },
  },
  [MQ.TABLET]: {
    [`html[style*="${FONT_SIZES[1].size}"] &`]: {
      '& > div': {
        marginRight: 20,
      },
      '& > div:last-child': {
        marginRight: 0,
      },
      display: 'flex',
    },
  },
  '& > div': {
    marginRight: 20,
  },
  '& > div:last-child': {
    marginRight: 0,
  },
  '& ~ label': {
    ...labelWhenSmallProps,
  },
  display: 'flex',
  [`html[style*="${FONT_SIZES[1].size}"] &, html[style*="${FONT_SIZES[2].size}"] &`]: {
    '& > div': {
      display: 'block',
      marginBottom: 15,
      marginRight: 0,
    },
    display: 'block',
  },
})

export const DateContainer = styled.div<DateContainerProps>(
  {
    display: 'inline-block',
    flexGrow: 1,
    marginRight: 10,
    position: 'relative',
  },
  ({ theme }) => ({
    borderBottom: bottomBorderForType(ElementType.SELECT, theme),
    fontFamily: theme.fontFamily,
  }),
)
DateContainer.displayName = 'DateContainer'

const DateSelect = styled.select<DateSelectProps>(
  {
    '&::-ms-expand': {
      display: 'none',
    },
    '&:disabled': {
      cursor: 'not-allowed',
    },
    appearance: 'none',
    display: 'block',
    paddingBottom: 10,
    width: '100%',
  },
  ({ hasLeftIcon, theme }) => ({
    color: theme.text,
    fontFamily: theme.fontFamily,
    paddingLeft: hasLeftIcon ? 30 : 0,
  }),
)
DateSelect.displayName = 'DateSelect'

export default DateElement
